// ============================== [AI Video API 服務層] ==============================
// 專注於 AI 視頻相關業務邏輯，使用底層的 apiClient 提供的 axios 配置和認證

import { createApiClient, AuthConfig } from './apiClient';
import { AxiosInstance } from 'axios';

// ============================== [類型定義] ==============================

// AI Video API 配置
interface AiVideoConfig {
  baseUrl: string;
  adminToken: string;
}

// 任務狀態類型
interface TaskStatus {
  status: string;
  [key: string]: any;
}

// ============================== [工具函數] ==============================

// 將 blob url 轉換為 blob
const changeBlobUrl2Blob = async (blobUrl: string): Promise<Blob> => {
  try {
    const response = await fetch(blobUrl);
    const blob = await response.blob();
    return blob;
  } catch (error) {
    console.error('changeBlobUrl2Blob error:', error);
    throw error;
  }
};

// ============================== [配置管理] ==============================

// 獲取 AI Video API 配置
const getAiVideoConfig = (): AiVideoConfig => {
  // 嘗試從多個來源獲取配置
  let baseUrl = process.env.REACT_APP_AI_VIDEO_API_URL;
  let adminToken = process.env.REACT_APP_AI_VIDEO_ADMIN_TOKEN || 'admin';
  
  // 如果沒有環境變數，使用默認配置
  if (!baseUrl) {
    // AI Video API 的正確 baseUrl
    baseUrl = 'https://ai-video.scfg.io';
  }
  
  return {
    baseUrl,
    adminToken
  };
};

// 創建 AI Video API 專用的認證配置
const createAiVideoAuthConfig = (): AuthConfig => {
  const config = getAiVideoConfig();
  
  return {
    mode: 'custom',
    customAuthFn: async () => {
      return {
        accessToken: config.adminToken,
        serverUrl: config.baseUrl
      };
    }
  };
};

// ============================== [任務狀態常量] ==============================

const DEFAULT_STATUS = {
  PROCESSING: "processing",
  FAILED: "failed",
  COMPLETED: "completed",
};

const HEYGEN_STATUS = {
  PROCESSING: "waiting",
  FAILED: "failed",
  COMPLETED: "completed",
};

const HEADRA_STATUS = {
  PROCESSING: "IN_PROGRESS",
  FAILED: "FAILED",
  COMPLETED: "COMPLETED",
};

const HAILUO_STATUS = {
  PROCESSING: "Processing",
  FAILED: "Failed",
  COMPLETED: "Success",
};

const COMBINED_STATUS = {
  PROCESSING: "starting",
  FAILED: "failed",
  COMPLETED: "completed",
};

// ============================== [輪詢檢查狀態] ==============================

const checkStatus = async (client: AxiosInstance, requestOptions: any): Promise<TaskStatus> => {
  try {
    const response = await client(requestOptions);

    switch (response.data.status) {
      case DEFAULT_STATUS.PROCESSING:
      case COMBINED_STATUS.PROCESSING:
        break;
      case DEFAULT_STATUS.FAILED:
      case DEFAULT_STATUS.COMPLETED:
      case HAILUO_STATUS.FAILED:
      case HAILUO_STATUS.COMPLETED:
      case COMBINED_STATUS.FAILED:
      case COMBINED_STATUS.COMPLETED:
        return response.data;
      default:
        break;
    }
  } catch (err) {
    throw err;
  }
  // 重新輪詢
  return new Promise((resolve) => {
    setTimeout(() => resolve(checkStatus(client, requestOptions)), 10000);
  });
};

// ============================== [配置化 API 工廠函數] ==============================

// 創建配置化的 aiVideoApi 實例
export const createAiVideoApi = (authConfig?: AuthConfig) => {
  // 使用提供的配置或默認的 AI Video 配置
  const finalAuthConfig = authConfig || createAiVideoAuthConfig();
  
  // 創建配置化的 client 獲取函數
  const getClient = async (): Promise<AxiosInstance> => {
    const client = await createApiClient(finalAuthConfig);
    
    // 為 AI Video API 添加特殊的請求攔截器
    client.interceptors.request.use((config) => {
      // 使用 X-ADMIN-TOKEN 而不是 Bearer token
      if (config.headers?.['Authorization']) {
        const token = config.headers['Authorization'].toString().replace('Bearer ', '');
        delete config.headers['Authorization'];
        config.headers['X-ADMIN-TOKEN'] = token;
      }
      return config;
    });
    
    return client;
  };

  return {
    // ============================== [FishAudio] ==============================
    fetchFishAudio: async ({ text, file }: { text: string; file: any }) => {
      try {
        const client = await getClient();
        const queryParams = new URLSearchParams({
          text: text,
          model_name: "model_name",
          model_description: "model_description",
          delete_model: 'true',
        });
        const url = `/root/fishaudio/full_tts?${queryParams}`;
        const blob = await changeBlobUrl2Blob(file.url);
        const formData = new FormData();
        formData.append("audio_file", blob);
        const response = await client.post(url, formData);

        if (response.status !== 200) {
          throw new Error("失敗: 文字轉語音");
        }

        return {
          orders: response.data?.task_id ?? "",
          message: response.data?.message ?? "",
        };
      } catch (error: any) {
        throw error.response?.status === 404
          ? new Error("找不到語音生成功能")
          : error;
      }
    },

    getFishAudioStatus: async (taskId: string) => {
      try {
        const client = await getClient();
        const requestOptions = {
          url: `/root/fishaudio/full_tts/status/${taskId}`,
          method: "GET",
        };
        const result = await checkStatus(client, requestOptions);
        return result;
      } catch (error) {
        throw error;
      }
    },

    getFishAudioResult: async (taskId: string) => {
      const client = await getClient();
      const apiurl = `/root/fishaudio/full_tts/result/${taskId}`;
      try {
        const response = await client.get(apiurl, {
          responseType: "blob",
        });
        return response.data;
      } catch (error) {
        throw error;
      }
    },

    // ============================== [ElevenLabs] ==============================
    createElevenLabsVoice: async ({ file }: { file: any }) => {
      try {
        const client = await getClient();
        const queryParams = new URLSearchParams({ audio_name: "audio_name" });
        const apiurl = `/root/elevenlabs/voice/create?${queryParams}`;
        const blob = await changeBlobUrl2Blob(file.url);
        const formData = new FormData();
        formData.append("audio_file", blob);
        const response = await client.post(apiurl, formData);
        return response.data;
      } catch (error) {
        throw error;
      }
    },

    postElevenLabsVoice: async ({ voice_id, file }: { voice_id: string; file: any }) => {
      try {
        const client = await getClient();
        const queryParams = new URLSearchParams({ voice_id: voice_id });
        const apiurl = `/root/elevenlabs/voice/full?${queryParams}`;
        const blob = await changeBlobUrl2Blob(file.url);
        const formData = new FormData();
        formData.append("audio_file", blob);
        const response = await client.post(apiurl, formData);
        return response.data;
      } catch (error) {
        throw error;
      }
    },

    getElevenLabsVoice: async (taskId: string) => {
      const client = await getClient();
      let isProcessing = true;
      while (isProcessing) {
        try {
          const queryParams = new URLSearchParams({ ticket_id: taskId });
          const apiurl = `/root/elevenlabs/voice/full?${queryParams}`;
          const response = await client.get(apiurl, {
            responseType: "blob",
          });
          isProcessing = false;
          return response.data;
        } catch (error: any) {
          if (!error.message.includes("任務尚未完成或已失敗")) {
            await new Promise((resolve) => setTimeout(resolve, 1000));
          } else {
            isProcessing = false;
          }
        }
      }
    },

    // ============================== [HeyGen] ==============================
    postHeyGenVideo: async ({ height, width, audio, image }: { height: number; width: number; audio: any; image: any }) => {
      try {
        const client = await getClient();
        const queryParams = new URLSearchParams({ height: height.toString(), width: width.toString() });
        const apiurl = `/root/heygen/heygen_full?${queryParams}`;
        const formData = new FormData();
        const blobAudio = await changeBlobUrl2Blob(audio.url);
        const blobImage = await changeBlobUrl2Blob(image.url);
        formData.append("audio_file", blobAudio);
        formData.append("image_file", blobImage);
        const response = await client.post(apiurl, formData);
        return response.data;
      } catch (error) {
        throw error;
      }
    },

    getHeyGenVideo: async (videoId: string) => {
      const client = await getClient();
      let isProcessing = true;
      while (isProcessing) {
        try {
          const apiurl = `/root/heygen/video_status/${videoId}`;
          const response = await client.get(apiurl, {
            responseType: "blob",
          });

          const contentType = response.headers["content-type"];
          if (contentType.includes("video/")) {
            isProcessing = false;
            return response.data;
          }

          switch (response.data?.data?.status) {
            case HEYGEN_STATUS.PROCESSING:
              await new Promise((resolve) => setTimeout(resolve, 1000));
              break;
            case HEYGEN_STATUS.FAILED:
              isProcessing = false;
              throw new Error(response.data.error?.message || "視頻生成失敗");
            default:
              await new Promise((resolve) => setTimeout(resolve, 1000));
          }
        } catch (error) {
          throw error;
        }
      }
    },

    // ============================== [Hedra] ==============================
    postHedraVideo: async ({ audio, image }: { audio: any; image: any }) => {
      try {
        const client = await getClient();
        const apiurl = `/root/hedra/generate_video`;
        const formData = new FormData();
        const blobAudio = await changeBlobUrl2Blob(audio.url);
        const blobImage = await changeBlobUrl2Blob(image.url);
        formData.append("audio_file", blobAudio);
        formData.append("image_file", blobImage);
        const response = await client.post(apiurl, formData);
        return response.data;
      } catch (error) {
        throw error;
      }
    },

    getHedraVideo: async (taskId: string) => {
      const client = await getClient();
      let isProcessing = true;
      while (isProcessing) {
        try {
          const apiurl = `/root/hedra/get_video?task_id=${taskId}`;
          const response = await client.get(apiurl, {
            responseType: "blob",
          });

          const contentType = response.headers["content-type"];
          if (contentType.includes("video/")) {
            isProcessing = false;
            return response.data;
          }

          switch (response.data?.status) {
            case HEADRA_STATUS.PROCESSING:
              await new Promise((resolve) => setTimeout(resolve, 1000));
              break;
            case HEADRA_STATUS.FAILED:
              isProcessing = false;
              throw new Error(response.data.error?.message || "視頻生成失敗");
            default:
              await new Promise((resolve) => setTimeout(resolve, 1000));
          }
        } catch (error) {
          throw error;
        }
      }
    },

    // ============================== [Hailuo] ==============================
    postHailuoVideo: async ({ action, image }: { action: string; image: any }) => {
      try {
        const client = await getClient();
        const apiurl = `/root/hailuo/hailuo_generate_video?text=${action}`;
        const formData = new FormData();
        const blob = await changeBlobUrl2Blob(image.url);
        formData.append("image", blob);
        const response = await client.post(apiurl, formData);
        return response.data;
      } catch (error) {
        throw error;
      }
    },

    getHailuoVideo: async (taskId: string) => {
      try {
        const client = await getClient();
        const apiurl = `/root/hailuo/hailuo_get_video?task_id=${taskId}`;
        return await checkStatus(client, { url: apiurl, method: "GET" });
      } catch (error) {
        throw error;
      }
    },

    getHailuoVideoResult: async (id: string) => {
      try {
        const client = await getClient();
        const apiurl = `/root/hailuo/hailuo_get_video_url?file_id=${id}`;
        const response = await client.get(apiurl, {
          responseType: "blob",
        });
        return response.data;
      } catch (error) {
        throw error;
      }
    },

    // ============================== [Livestream] ==============================
    // 獲取所有直播房間（分頁）
    getAllLivestreamRooms: async ({ page = 1, limit = 20 }: { page?: number; limit?: number } = {}) => {
      try {
        const client = await getClient();
        const queryParams = new URLSearchParams({ page: page.toString(), limit: limit.toString() });
        const apiurl = `/root/lifestream/get_all_livestream_rooms?${queryParams}`;
        const response = await client.get(apiurl);
        return response.data;
      } catch (error) {
        throw error;
      }
    },

    // 創建直播房間
    createLivestreamRoom: async ({ livestreamId, livestreamUrl }: { livestreamId: string; livestreamUrl: string }) => {
      try {
        const client = await getClient();
        const queryParams = new URLSearchParams({
          livestream_id: livestreamId,
          livestream_url: livestreamUrl,
        });
        const apiurl = `/root/lifestream/create_livestream_room?${queryParams}`;
        const response = await client.post(apiurl);
        return response.data;
      } catch (error) {
        throw error;
      }
    },

    // 更新直播房間添加URL
    addRoomUrl: async ({ roomCode, url }: { roomCode: string; url: string }) => {
      try {
        const client = await getClient();
        const queryParams = new URLSearchParams({
          room_code: roomCode,
          url: url,
        });
        const apiurl = `/root/lifestream/add_room_url?${queryParams}`;
        const response = await client.put(apiurl);
        return response.data;
      } catch (error) {
        throw error;
      }
    },

    // 獲取通過房間代碼獲取直播房間和所有觀眾
    getLivestreamRoom: async (roomCode: string) => {
      try {
        const client = await getClient();
        const queryParams = new URLSearchParams({ room_code: roomCode });
        const apiurl = `/root/lifestream/get_livestream_room?${queryParams}`;
        const response = await client.get(apiurl);
        return response.data;
      } catch (error) {
        throw error;
      }
    },

    // 新增觀眾到直播房間
    addViewer: async ({ name, roomCode }: { name: string; roomCode: string }) => {
      try {
        const client = await getClient();
        const queryParams = new URLSearchParams({
          name: name,
          room_code: roomCode,
        });
        const apiurl = `/root/lifestream/add_viewer?${queryParams}`;
        const response = await client.post(apiurl);
        return response.data;
      } catch (error) {
        throw error;
      }
    },

    // 移除直播房間的room_code，但保留其他資料
    removeRoomCode: async (id: string) => {
      try {
        const client = await getClient();
        const queryParams = new URLSearchParams({ id: id });
        const apiurl = `/root/lifestream/remove_room_code?${queryParams}`;
        const response = await client.put(apiurl);
        return response.data;
      } catch (error) {
        throw error;
      }
    },

    // 獲取根據觀眾ID獲取觀眾資訊
    getViewer: async (viewerId: string) => {
      try {
        const client = await getClient();
        const apiurl = `/root/lifestream/viewer/${viewerId}`;
        const response = await client.get(apiurl);
        return response.data;
      } catch (error) {
        throw error;
      }
    },

    // 獲取特定直播房間的所有觀眾
    getRoomViewers: async (roomCode: string) => {
      try {
        const client = await getClient();
        const queryParams = new URLSearchParams({ room_code: roomCode });
        const apiurl = `/root/lifestream/get_room_viewers?${queryParams}`;
        const response = await client.get(apiurl);
        return response.data;
      } catch (error) {
        throw error;
      }
    },

    // 刪除特定觀眾
    deleteViewer: async (viewerId: string) => {
      try {
        const client = await getClient();
        const apiurl = `/root/lifestream/delete_viewer/${viewerId}`;
        const response = await client.delete(apiurl);
        return response.data;
      } catch (error) {
        throw error;
      }
    },

    // 創建在直播間發送訊息
    sendMessage: async ({ roomCode, action, content, viewerId }: { roomCode: string; action: string; content: string; viewerId: string }) => {
      try {
        const client = await getClient();
        const queryParams = new URLSearchParams({
          action: action,
          content: content,
          viewer_id: viewerId,
        });
        const apiurl = `/root/lifestream/send_message/${roomCode}?${queryParams}`;
        const response = await client.post(apiurl);
        return response.data;
      } catch (error) {
        throw error;
      }
    },

    // 獲取直播間的訊息
    getMessages: async ({ roomCode, limit = 50 }: { roomCode: string; limit?: number }) => {
      try {
        const client = await getClient();
        const queryParams = new URLSearchParams({ limit: limit.toString() });
        const apiurl = `/root/lifestream/get_messages/${roomCode}?${queryParams}`;
        const response = await client.get(apiurl);
        return response.data;
      } catch (error) {
        throw error;
      }
    },

    // 獲取通過房間ID獲取直播間的訊息
    getMessagesById: async ({ roomId, limit = 50 }: { roomId: string; limit?: number }) => {
      try {
        const client = await getClient();
        const queryParams = new URLSearchParams({ limit: limit.toString() });
        const apiurl = `/root/lifestream/get_messages_by_id/${roomId}?${queryParams}`;
        const response = await client.get(apiurl);
        return response.data;
      } catch (error) {
        throw error;
      }
    },

    // 清理指定天數之前的舊訊息
    cleanupOldMessages: async ({ roomCode, days = 7 }: { roomCode: string; days?: number }) => {
      try {
        const client = await getClient();
        const queryParams = new URLSearchParams({ days: days.toString() });
        const apiurl = `/root/lifestream/cleanup_old_messages/${roomCode}?${queryParams}`;
        const response = await client.delete(apiurl);
        return response.data;
      } catch (error) {
        throw error;
      }
    },

    // ============================== [Combined] ==============================
    // 結合換臉、虛擬試穿和去背景功能
    postCombinedFaceOutfitSwapWithRembg: async ({ faceImage, garmentImage, targetImage, category }: { faceImage: any; garmentImage: any; targetImage: any; category: string }) => {
      try {
        const client = await getClient();
        const queryParams = new URLSearchParams({ category: category });
        const apiurl = `/root/combined/face_outfit_swap_with_rembg?${queryParams}`;
        const formData = new FormData();

        const faceBlob = await changeBlobUrl2Blob(faceImage.url);
        const garmentBlob = await changeBlobUrl2Blob(garmentImage.url);
        const targetBlob = await changeBlobUrl2Blob(targetImage.url);

        formData.append("face_image", faceBlob);
        formData.append("garment_image", garmentBlob);
        formData.append("target_image", targetBlob);

        const response = await client.post(apiurl, formData);
        return response.data;
      } catch (error) {
        throw error;
      }
    },

    // 獲取換臉換衣服去背的最終結果
    getCombinedRembgResult: async (taskId: string) => {
      const client = await getClient();
      let isProcessing = true;
      while (isProcessing) {
        try {
          const apiurl = `/root/combined/get_rembg_result/${taskId}`;
          const response = await client.get(apiurl, {
            responseType: "blob",
          });

          const contentType = response.headers["content-type"];
          if (contentType.includes("image/")) {
            isProcessing = false;
            return response.data;
          }

          switch (response.data?.status) {
            case COMBINED_STATUS.PROCESSING:
              await new Promise((resolve) => setTimeout(resolve, 1000));
              break;
            case COMBINED_STATUS.FAILED:
              isProcessing = false;
              throw new Error(response.data.error?.message || "換臉換衣服去背生成失敗");
            default:
              await new Promise((resolve) => setTimeout(resolve, 1000));
          }
        } catch (error) {
          throw error;
        }
      }
    },
  };
};

// ============================== [測試環境配置] ==============================

// 測試環境專用 aiVideoApi 創建函數
export const createTestAiVideoApi = (baseUrl: string, adminToken: string = 'admin') => {
  const testAuthConfig: AuthConfig = {
    mode: 'custom',
    customAuthFn: async () => {
      return {
        accessToken: adminToken,
        serverUrl: baseUrl
      };
    }
  };
  
  return createAiVideoApi(testAuthConfig);
};

// ============================== [默認 API 實例] ==============================
// 向後兼容：使用默認配置
export const aiVideoApi = createAiVideoApi();

// 測試環境實例 - 使用實際的 API URL
export const testAiVideoApi = createTestAiVideoApi(
  'https://os.api.scfg.io',
  'admin'
); 